import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, ShoppingCart, RotateCcw, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DC_STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700',
  'Converted': 'bg-green-100 text-green-700',
  'Partially Converted': 'bg-amber-100 text-amber-700',
  'Returned': 'bg-slate-100 text-slate-700',
};

export const DeliveryChallanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // Conversion modal
  const [showConvert, setShowConvert] = useState(false);
  const [convertDefault, setConvertDefault] = useState('sale'); // 'sale' or 'return'
  const [dcItems, setDcItems] = useState([]); // from GET /dc/{id}/items
  const [itemActions, setItemActions] = useState({}); // { item_id: 'sale' | 'return' }
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/delivery-challans/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders())
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
      } catch (err) { setError('Failed to load delivery challan'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, lid, field = 'name') => list.find(i => i.id === lid)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const openConvertModal = async (defaultAction) => {
    setConvertDefault(defaultAction);
    setConvertError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/delivery-challans/${id}/items`, getAuthHeaders());
      setDcItems(res.data);
      const actions = {};
      res.data.forEach(group => {
        group.item_ids.forEach(it => {
          if (it.status === 'assigned_to_dc') {
            actions[it.item_id] = defaultAction;
          }
        });
      });
      setItemActions(actions);
      setShowConvert(true);
    } catch { setConvertError('Failed to load DC items'); }
  };

  const toggleAction = (itemId) => {
    setItemActions(prev => ({
      ...prev,
      [itemId]: prev[itemId] === 'sale' ? 'return' : 'sale'
    }));
  };

  const handleConvert = async () => {
    setConvertError('');
    const items = [];
    dcItems.forEach(group => {
      group.item_ids.forEach(it => {
        if (it.status === 'assigned_to_dc' && itemActions[it.item_id]) {
          items.push({
            product_id: group.product_id,
            item_id: it.item_id,
            action: itemActions[it.item_id],
          });
        }
      });
    });
    if (!items.length) { setConvertError('No actionable items'); return; }
    setConvertSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/delivery-challans/${id}/convert`, { items }, getAuthHeaders());
      if (res.data.has_sale) {
        // Save prefill to sessionStorage and navigate to sale create
        sessionStorage.setItem('dc_sale_prefill', JSON.stringify(res.data.sale_prefill));
        navigate('/admin/sale/invoices/new?from_dc=' + id);
      } else {
        // Pure return — reload DC
        const dcRes = await axios.get(`${BACKEND_URL}/api/delivery-challans/${id}`, getAuthHeaders());
        setDoc(dcRes.data);
        setShowConvert(false);
      }
    } catch (err) {
      setConvertError(err.response?.data?.detail || 'Conversion failed');
    } finally { setConvertSubmitting(false); }
  };

  const saleCount = Object.values(itemActions).filter(a => a === 'sale').length;
  const returnCount = Object.values(itemActions).filter(a => a === 'return').length;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error && !doc) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  const isConverted = doc.status === 'Converted' || doc.status === 'Returned';

  return (
    <div data-testid="delivery-challan-detail-page">
      <button onClick={() => navigate('/admin/sale/delivery-challan')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Delivery Challans
      </button>

      {/* Header + Action Buttons */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Delivery Challan Details</h1>
          {doc.status && doc.status !== 'Open' && (
            <span className={`px-3 py-1 text-xs font-bold uppercase rounded ${DC_STATUS_STYLES[doc.status] || 'bg-slate-100 text-slate-700'}`}>{doc.status}</span>
          )}
        </div>
        {!isConverted && (
          <div className="flex items-center gap-2">
            <button onClick={() => openConvertModal('sale')} data-testid="convert-sale-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-green-700 transition-colors">
              <ShoppingCart className="h-3.5 w-3.5" /> Convert to Sale
            </button>
            <button onClick={() => openConvertModal('return')} data-testid="return-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-slate-700 transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> Return
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Challan Number</span><span className="text-sm font-semibold text-slate-900">{doc.challan_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Challan Date</span><span className="text-sm text-slate-900">{formatDate(doc.challan_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Due Date</span><span className="text-sm text-slate-900">{formatDate(doc.due_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Customer</span><span className="text-sm text-slate-900">{doc.customer_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* ========== CONVERSION MODAL ========== */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                {convertDefault === 'sale' ? 'Convert to Sale' : 'Return Items'}
              </h2>
              <button onClick={() => setShowConvert(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              {convertError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-5 w-5" /><span>{convertError}</span>
                </div>
              )}

              <p className="text-sm text-slate-500 mb-4">
                Toggle each item as <span className="font-semibold text-green-700">Sale</span> or <span className="font-semibold text-slate-600">Return</span>. Every item must be accounted for.
              </p>

              {dcItems.map((group, gIdx) => {
                const actionableItems = group.item_ids.filter(it => it.status === 'assigned_to_dc');
                if (!actionableItems.length) return null;
                return (
                  <div key={gIdx} className="mb-5">
                    <h3 className="text-sm font-bold text-slate-900 mb-2">{group.product_name || 'Product'}</h3>
                    <div className="space-y-1.5">
                      {actionableItems.map(it => (
                        <div key={it.item_id} className="flex items-center justify-between px-4 py-2.5 border border-slate-200 rounded-sm hover:bg-slate-50">
                          <span className="font-mono text-sm text-slate-800">{it.item_id}</span>
                          <button type="button"
                            onClick={() => toggleAction(it.item_id)}
                            data-testid={`toggle-${it.item_id}`}
                            className={`px-4 py-1.5 text-xs font-bold uppercase rounded-sm transition-colors ${
                              itemActions[it.item_id] === 'sale'
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                            }`}>
                            {itemActions[it.item_id] === 'sale' ? 'Sale' : 'Return'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Summary */}
              <div className="flex items-center gap-4 pt-4 border-t border-slate-200 text-sm">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded font-bold">{saleCount} Sale</span>
                <span className="bg-slate-200 text-slate-600 px-3 py-1 rounded font-bold">{returnCount} Return</span>
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button onClick={() => setShowConvert(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={handleConvert} disabled={convertSubmitting || (!saleCount && !returnCount)}
                  data-testid="convert-confirm-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {convertSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
